import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChipSelect } from '@elements';
import {
  useHealthQuoteStore,
  HealthAddOnSubOptionValue,
  HealthMember,
  HealthAddOn,
  HealthMemberAddOnSubOptionKey
} from '@store/healthquote';
import { Box, Text, Button, Stack, Group, Checkbox } from '@ui';
import { OPD } from '@utils/constants';

interface OptionProps {
  options: HealthAddOnSubOptionValue[];
  addOn: HealthAddOn;
  addOnName: string;
  onClose: () => void;
}

const ABHIAddonSubOptions: FC<OptionProps> = ({
  options,
  addOn: selectedAddOn,
  addOnName,
  onClose
}) => {
  const {
    plan,
    addOns,
    toggleModalAddOn,
    setDraftMemberDiseases,
    setDraftMemberAddOns
  } = useHealthQuoteStore((state) => ({
    plan: state.plan,
    toggleModalAddOn: state.toggleModalAddOn,
    setDraftMemberDiseases: state.setDraftMemberDiseases,
    setDraftMemberAddOns: state.setDraftMemberAddOns,
    addOns: state.addOns
  }));

  const [members, setMembers] = useState<HealthMember[]>([]);

  const notAdultsRef = useRef<any[]>([]);

  const addOnKey = selectedAddOn.key;

  useEffect(() => {
    let _members = plan.draftMembers || [];

    //For opd_care rider we need to filter out members with less than 18 years age.
    if (addOnKey === OPD) {
      const notAdults = _members.filter((item) => item && item.age && item.age < 18);

      if (notAdults.length) {
        notAdultsRef.current = [...notAdults];
      }

      _members = _members.filter((item) => item && item.age && item.age >= 18);
    }

    setMembers(_members.map((member, id) => ({ ...member, id: id })));
  }, [plan.draftMembers]);

  const handleDiseaseChange = (diseases: string[] = [], memberId: number) => {
    setDraftMemberDiseases(memberId, diseases);
  };

  const isButtonDisabled = () => {
    // Check if every selected member has at least one disease selected
    const allSelectedMembersHaveDiseases = members
      .filter(
        (member) =>
          member.addons &&
          member.addons.find((addOn) => addOn.key === addOnKey && addOn.included)
      )
      .every(
        (member) => Array.isArray(member?.diseases) && member.diseases.length > 0
      );

    return !allSelectedMembersHaveDiseases;
  };

  const atLeastOneMemberSelected = useMemo(() => {
    return members.some(
      (member) =>
        member.addons &&
        member.addons.some((addOn) => addOn.key === addOnKey && addOn.included)
    );
  }, [members]);

  const getText = (value: string) => {
    if (value === OPD) {
      return ['Chronic Management Program (OPD) rider', 'Chronic Care rider'];
    }
    return ['Chronic Care rider', 'Chronic Management Program (OPD) rider'];
  };

  const confirmAction = useCallback(
    (clear?: boolean) => {
      let modifiedMembers = members;

      // If opd_care rider is selected, add NotAdults to the list of members
      if (addOnKey === OPD && notAdultsRef.current.length) {
        modifiedMembers = [...members, ...notAdultsRef.current];
      }

      const updatedMembers = modifiedMembers.map((member) => ({
        ...member,
        [addOnKey]:
          member?.addons?.find((addOn) => addOn.key === addOnKey)?.included || false,
        addons: member.addons
          ? member.addons.map((addOn) =>
              addOn.key === addOnKey
                ? {
                    ...addOn,
                    sub_options: addOn.included
                      ? member.diseases?.map((disease) => ({
                          key: 'med_conditions' as HealthMemberAddOnSubOptionKey,
                          value: disease
                        })) || []
                      : []
                  }
                : addOn
            )
          : []
      }));

      toggleModalAddOn(
        addOnKey,
        updatedMembers,
        !clear ? atLeastOneMemberSelected : false
      );
      onClose();
    },
    [atLeastOneMemberSelected, members, addOnKey]
  );

  const handleMemberCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    memberId: number | undefined
  ) => {
    const { checked } = e.target;
    const member = members.find((m) => m.id === memberId);
    if (checked) {
      setDraftMemberAddOns(memberId!, [
        ...(member?.addons || []),
        {
          key: addOnKey,
          included: checked,
          sub_options:
            member?.diseases?.map((disease) => ({
              key: 'med_conditions',
              value: disease
            })) || []
        }
      ]);
    } else {
      const filteredAddOns =
        member?.addons?.filter((addOn) => addOn.key !== addOnKey) || [];

      setDraftMemberAddOns(memberId!, filteredAddOns);
      const noDefaultOptionAddOnsIncluded = addOns
        .filter((addOn) => addOn.noDefaultOption && addOn.included)
        .map((addOn) => addOn.key);
      if (
        !filteredAddOns.some((addOn) =>
          noDefaultOptionAddOnsIncluded.includes(addOn.key)
        )
      ) {
        setDraftMemberDiseases(memberId!, []);
      }
    }
  };

  return (
    <Stack gap={24}>
      <Stack gap="sm">
        <Text fz={{ base: 24, sm: 28 }} fw={600} lh={1.1}>
          {addOnName}
        </Text>
        <Text c="secondary" fz={14}>
          Any disease that you select / deselect in {getText(addOnKey)[0]} will
          reflect in {getText(addOnKey)[1]} and vice versa. (Max 3 diseases per
          person)
        </Text>
      </Stack>

      {members.map((member) => {
        const selectedMemberAddon = member.addons
          ? member.addons?.find((addOn) => addOn.key === addOnKey)
          : null;
        return (
          <Group
            key={member.relation + member.id}
            gap="sm"
            align="flex-start"
            justify="flex-start"
            wrap="nowrap"
          >
            <Checkbox
              checked={selectedMemberAddon?.included || false}
              // readOnly
              mt="4"
              // readonly seems to allow click events, so we prevent default to avoid any action
              onChange={(e) => handleMemberCheckboxChange(e, member.id)}
            />
            <ChipSelect
              label={`Select conditions for ${member.relation} (Max 3)`}
              data={options}
              onChange={(values) => {
                handleDiseaseChange(values, member.id!);
              }}
              maxSelections={3}
              value={member.diseases || []}
              disabled={!selectedMemberAddon?.included || false}
            />
          </Group>
        );
      })}

      <Box mt={20} w="100%">
        {atLeastOneMemberSelected ? (
          <Button
            fullWidth
            disabled={isButtonDisabled()}
            onClick={() => confirmAction(false)}
          >
            Confirm
          </Button>
        ) : (
          <Button
            variant="outline"
            fullWidth
            color="secondary"
            onClick={() => confirmAction(true)}
          >
            Close
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export { ABHIAddonSubOptions };
